import { useState, useMemo, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  FormControl,
  ListItemText,
  OutlinedInput,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import NowrapWrapper from '../../../components/NowrapWrapper';
import FunctionalTable from '../../../components/FunctionalTable';
import OverLayLoader from '../../../components/OverLayLoader';
import { SelectColumnFilter } from '../../../components/ColumnFilter';
import { EditableCell } from '../../../components/EditableCell';
import { getLanguages, getProfiles } from '../../../services';
import { getUsersList, updateUsers } from '../../../services/users';
import { CONSTANT_KEYS } from '../../../helpers/tableKeys';
import { ACTIVE_OPTIONS } from '../../../helpers/options';
import statusCodes from '../../../utils/statusCodes';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import useTitle from '../../../hooks/useTitle';

const {
  ACTIVE,
  PROFILES,
  USERNAME,
  ACCESS_LEVEL,
  AVAILABLE_PROFILES,
  DATA_LANGUAGE_ID,
  OFFSET,
  LIMIT,
  DEFAULT_PROFILE,
  COMMENT,
} = CONSTANT_KEYS;

const mergeProfiles = (prevProfiles, selectedNames, allProfiles) => {
  return selectedNames.map(name => {
    const baseProfile = allProfiles.find(p => p.name === name);
    const existing = prevProfiles.find(p => p.profile_name === name);

    if (!baseProfile) {
      return {
        profile_name: name,
        is_default: existing ? existing.is_default : false,
      };
    }

    return {
      ...baseProfile,
      profile_name: name,
      is_default: existing ? existing.is_default : false,
    };
  });
};

const UsersListSettingsView = () => {
  const [users, setUsers] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [languageList, setLanguageList] = useState(null);
  const [profiles, setProfiles] = useState([]);

  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const location = useLocation();

  const { t } = useTranslation(['users']);

  const title = t('common:pages.settings.primary');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  useEffect(() => {
    if (isOptionsLoading) {
      return;
    }
  }, [isOptionsLoading]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const [languages, allProfiles] = await Promise.all([
          getLanguages(),
          getProfiles(),
        ]);
        setLanguageList(languages.data);
        setProfiles(allProfiles.data);
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsOptionsLoading(false);
      }
    };
    fetchFilterOptions();
  }, []);

  const fetchData = useCallback(
    async ({ params, signal }) => {
      if (!languageList) {
        return;
      }

      setIsLoading(true);

      const newParams = {
        [OFFSET]: params[OFFSET],
        [LIMIT]: params[LIMIT],
        ...(params[USERNAME]
          ? {
              username: params[USERNAME],
            }
          : {}),
        ...(params[ACTIVE]
          ? {
              is_active: params[ACTIVE],
            }
          : {}),
        ...(params[DATA_LANGUAGE_ID]
          ? {
              data_language_id: languageList.find(
                lng => lng.name.toUpperCase() === params[DATA_LANGUAGE_ID],
              )?.id,
            }
          : {}),
        ...(params[AVAILABLE_PROFILES] && params[AVAILABLE_PROFILES]?.length > 0
          ? Array.isArray(params[AVAILABLE_PROFILES])
            ? { available_profiles: params[AVAILABLE_PROFILES] }
            : { available_profiles: params[AVAILABLE_PROFILES].split(',') }
          : {}),
        ...(params[DEFAULT_PROFILE]
          ? { default_profile: params[DEFAULT_PROFILE] }
          : {}),
        ...(params[COMMENT]
          ? {
              comment: params[COMMENT],
            }
          : {}),
      };

      try {
        const data = await getUsersList(newParams, signal);
        if (data) {
          const newData = data.data.map(el => ({ ...el, uniqId: el.id }));
          setUsers(prev =>
            params[OFFSET] === 0 ? [...newData] : [...prev, ...newData],
          );
          setHasMoreData(prev => (params[OFFSET] === 0 ? true : prev));
          if (data.data.length < params[LIMIT]) {
            setHasMoreData(false);
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.log(error.message);
        setIsLoading(false);
      }
    },
    [languageList],
  );

  // редактирование строк таблицы
  const editHandler = codeId => {
    setUsers(prev =>
      prev.map(el =>
        el.id === codeId ? { ...el, isEdit: true, prevState: el } : el,
      ),
    );
  };

  const handleChangeTableData = ({ index, value, columnName, isValid }) => {
    switch (columnName) {
      case ACTIVE:
      case DATA_LANGUAGE_ID: {
        setUsers(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [columnName]: value,
                }
              : row,
          ),
        );
        break;
      }

      case AVAILABLE_PROFILES: {
        setUsers(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [PROFILES]: mergeProfiles(
                    row[PROFILES] || [],
                    value,
                    profiles,
                  ),
                }
              : row,
          ),
        );
        break;
      }

      case DEFAULT_PROFILE: {
        setUsers(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [PROFILES]: row[PROFILES].map(pr => {
                    return value.includes(pr.profile_name)
                      ? { ...pr, is_default: true }
                      : { ...pr, is_default: false };
                  }),
                }
              : row,
          ),
        );
        break;
      }

      case COMMENT: {
        setUsers(prev =>
          prev.map(row =>
            index === row.id
              ? {
                  ...row,
                  [COMMENT]: value,
                }
              : row,
          ),
        );
        break;
      }

      default:
        return;
    }
  };

  const applyChangesHandler = async (codeId, data) => {
    try {
      setIsLoading(true);
      const newData = {
        data_language_id: data.data_language_id,
        is_active: data.is_active,
        is_superuser: data.is_superuser,
        profiles: data.profiles,
        username: data.username,
        comment: data.comment,
      };

      await updateUsers(codeId, newData);

      window.location.reload();
    } catch (error) {
      if (
        error?.response?.status === statusCodes.HTTP_422_UNPROCESSABLE_ENTITY &&
        error?.response?.data?.detail &&
        typeof error?.response?.data?.detail === 'string'
      ) {
        createErrorNotification(error.response.data.detail);
      } else {
        createErrorNotification();
      }
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelChangesHandler = codeId => {
    setUsers(prev =>
      prev.map(el => (el.id === codeId ? { ...el.prevState } : el)),
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t(`table.username`)} />,
        accessor: USERNAME,
        id: USERNAME,
        style: { width: '18%', maxWidth: '150px', minWidth: '150px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.active`)} />,
        accessor: ACTIVE,
        id: ACTIVE,
        style: { width: '10%', maxWidth: '90px', minWidth: '90px' },
        Filter: cell => {
          const options = ACTIVE_OPTIONS.map(opt => opt.label);
          return <SelectColumnFilter cell={cell} options={options} />;
        },
        Cell: ({
          value: initialValue,
          row: { index, original },
          column: { id: columnName },
        }) => {
          const [value, setValue] = useState(Boolean(initialValue));

          useEffect(() => {
            setValue(Boolean(initialValue));
          }, [initialValue]);

          const isEdit = original.isEdit;

          if (!isEdit) {
            return value ? 'YES' : 'NO';
          }

          return (
            <FormControl fullWidth variant="standard">
              <Select
                id={'Select-' + columnName + '-' + index}
                value={value}
                input={<OutlinedInput />}
                name={columnName + '-' + index}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ autoFocus: false, sx: { maxWidth: false } }}
                onChange={e => {
                  const selected = e.target.value;

                  setValue(selected);
                  handleChangeTableData({
                    index: original.uniqId,
                    value: selected,
                    columnName,
                  });
                }}
              >
                {ACTIVE_OPTIONS.map(({ label, value }) => (
                  <MenuItem key={label} value={value} title={label}>
                    <ListItemText primary={label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.access_level`)} />,
        accessor: ACCESS_LEVEL,
        id: ACCESS_LEVEL,
        Filter: () => null,
        style: { width: '15%', maxWidth: '150px', minWidth: '150px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.available_profiles`)} />,
        accessor: PROFILES,
        id: AVAILABLE_PROFILES,
        Filter: cell => {
          const profileNames = profiles.map(el => el.name);
          return (
            <SelectColumnFilter cell={cell} options={profileNames} multiple />
          );
        },
        style: { width: '18%', maxWidth: '150px', minWidth: '150px' },
        Cell: ({
          value: initialValue,
          row: { index, original },
          column: { id: columnName },
        }) => {
          const [value, setValue] = useState([]);

          useEffect(() => {
            const profilesNames = initialValue.map(el => el.profile_name);
            setValue(profilesNames);
          }, [initialValue]);

          const isEdit = original.isEdit;

          if (!isEdit) {
            return value.join('; ');
          }

          return (
            <FormControl fullWidth variant="standard">
              <Select
                id={'Select-' + columnName + '-' + index}
                value={value}
                input={<OutlinedInput />}
                name={columnName + '-' + index}
                multiple
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ autoFocus: false, sx: { maxWidth: false } }}
                renderValue={selected => selected.join('; ')}
                onChange={e => {
                  const selected = e.target.value || [];

                  setValue(selected);
                  handleChangeTableData({
                    index: original.uniqId,
                    value: selected,
                    columnName,
                  });
                }}
              >
                {profiles.map(({ name }) => (
                  <MenuItem key={name} value={name} title={name}>
                    <Checkbox
                      checked={value.indexOf(name) > -1}
                      className="column-filter-checkbox"
                    />

                    <ListItemText primary={name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.dictionary_language`)} />,
        accessor: DATA_LANGUAGE_ID,
        id: DATA_LANGUAGE_ID,
        style: { width: '12%', maxWidth: '90px', minWidth: '90px' },
        Filter: cell => {
          const options = languageList?.map(lng => lng.name?.toUpperCase());
          return <SelectColumnFilter cell={cell} options={options || []} />;
        },
        Cell: ({
          value: initialValue,
          row: { index, original },
          column: { id: columnName },
        }) => {
          const [value, setValue] = useState(initialValue);

          useEffect(() => {
            setValue(initialValue);
          }, [initialValue]);

          const isEdit = original.isEdit;

          if (!isEdit) {
            const lng = languageList.find(el => el.id === value);
            return lng?.name?.toUpperCase() || '';
          }

          return (
            <FormControl fullWidth variant="standard">
              <Select
                id={'Select-' + columnName + '-' + index}
                value={value}
                input={<OutlinedInput />}
                name={columnName + '-' + index}
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ autoFocus: false, sx: { maxWidth: false } }}
                onChange={e => {
                  const selected = e.target.value;

                  setValue(selected);
                  handleChangeTableData({
                    index: original.uniqId,
                    value: selected,
                    columnName,
                  });
                }}
              >
                {languageList.map(({ id, name }) => (
                  <MenuItem key={name} value={id} title={name}>
                    <ListItemText primary={name.toUpperCase()} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.profile`)} />,
        accessor: PROFILES,
        id: DEFAULT_PROFILE,
        style: { width: '12%', maxWidth: '100px', minWidth: '100px' },
        Filter: cell => {
          const profileNames = profiles.map(el => el.name);
          return <SelectColumnFilter cell={cell} options={profileNames} />;
        },
        Cell: ({
          value: initialValue,
          row: { index, original },
          column: { id: columnName },
        }) => {
          const [value, setValue] = useState([]);

          useEffect(() => {
            const defaultProfiles = initialValue.filter(el => el.is_default);
            const profilesNames = defaultProfiles.map(el => el.profile_name);
            setValue(profilesNames);
          }, [initialValue]);

          const isEdit = original.isEdit;

          if (!isEdit) {
            return value.join('; ');
          }

          return (
            <FormControl fullWidth variant="standard">
              <Select
                id={'Select-' + columnName + '-' + index}
                value={value}
                input={<OutlinedInput />}
                name={columnName + '-' + index}
                multiple
                IconComponent={KeyboardArrowDownIcon}
                MenuProps={{ autoFocus: false, sx: { maxWidth: false } }}
                renderValue={selected => selected.join('; ')}
                onChange={e => {
                  const selected = e.target.value || [];

                  setValue(selected);
                  handleChangeTableData({
                    index: original.uniqId,
                    value: selected,
                    columnName,
                  });
                }}
              >
                {initialValue.map(({ is_default, profile_name }) => (
                  <MenuItem
                    key={profile_name}
                    value={profile_name}
                    title={profile_name}
                  >
                    <Checkbox
                      checked={value.indexOf(profile_name) > -1}
                      className="column-filter-checkbox"
                    />

                    <ListItemText primary={profile_name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.comment`)} />,
        accessor: COMMENT,
        id: COMMENT,
        style: { width: '16%', maxWidth: '100px', minWidth: '100px' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value
              ) : (
                <EditableCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeTableData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, profiles, languageList, location.pathname],
  );

  return (
    <>
      {(isLoading || isOptionsLoading) && <OverLayLoader />}
      <Box>
        <FunctionalTable
          type="sticky"
          columns={columns}
          data={users}
          onEdit={editHandler}
          fetchData={fetchData}
          applyChanges={applyChangesHandler}
          cancelChanges={cancelChangesHandler}
          hasMoreData={hasMoreData}
          isInfiniteScroll
          pageSize={50}
        />
      </Box>
    </>
  );
};

export default UsersListSettingsView;
